import React, { useState, useEffect } from 'react';
import { shirtProductAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';

const DesignStep = ({ selectedDesign, setSelectedDesign, onNext, onBack }) => {
  const [shirts, setShirts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    fetchShirts();
  }, []);

  // Check for pre-selected shirt from localStorage
  useEffect(() => {
    if (shirts.length > 0 && !selectedDesign) {
      const preselectedShirtId = localStorage.getItem('preselectedShirtId');
      if (preselectedShirtId) {
        // Find the shirt with matching ID
        const preselectedShirt = shirts.find(shirt => shirt.id === parseInt(preselectedShirtId));
        if (preselectedShirt) {
          setSelectedDesign(preselectedShirt);
          // Clear the localStorage item after using it
          localStorage.removeItem('preselectedShirtId');
        }
      }
    }
  }, [shirts, selectedDesign, setSelectedDesign]);

  const fetchShirts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await shirtProductAPI.listProducts();
      setShirts(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching shirts:', err);
      setError('Failed to load shirt designs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchToRegister = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };

  const switchToLogin = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
  };

  const closeModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
  };

  const handleLoginSuccess = () => {
    closeModals();
    // Automatically proceed to next step after successful login
    setTimeout(() => {
      handleNext();
    }, 500); // Small delay to ensure auth state is updated
  };

  const handleNext = React.useCallback(() => {
    if (!selectedDesign) {
      return;
    }

    // Check if user is authenticated before proceeding
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    // Proceed to next step
    onNext();
  }, [selectedDesign, isAuthenticated, onNext]);

  // Auto advance only when design is newly selected
  const [shouldAutoAdvance, setShouldAutoAdvance] = useState(false);

  useEffect(() => {
    if (selectedDesign && isAuthenticated && shouldAutoAdvance) {
      handleNext();
      setShouldAutoAdvance(false);
    }
  }, [selectedDesign, isAuthenticated, handleNext, shouldAutoAdvance]);

  if (loading) {
    return (
      <div className="upload-section">
        <h2 style={{ fontSize: '24px', marginBottom: '10px', textAlign: 'center' }}>
          Choose Your Shirt Design
        </h2>
        <div className="loading">
          <div className="spinner"></div>
          <p style={{ color: '#718096', marginTop: '20px' }}>Loading shirt designs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="upload-section">
        <h2 style={{ fontSize: '24px', marginBottom: '10px', textAlign: 'center' }}>
          Choose Your Shirt Design
        </h2>
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          background: '#fef2f2',
          borderRadius: '12px',
          margin: '20px 0'
        }}>
          <p style={{ color: '#dc2626', marginBottom: '20px' }}>{error}</p>
          <button className="btn-primary" onClick={fetchShirts}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (shirts.length === 0) {
    return (
      <div className="upload-section">
        <h2 style={{ fontSize: '24px', marginBottom: '10px', textAlign: 'center' }}>
          Choose Your Shirt Design
        </h2>
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          background: '#f7fafc',
          borderRadius: '12px',
          margin: '20px 0'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>👕</div>
          <p style={{ color: '#718096', marginBottom: '20px' }}>
            No shirt designs available yet. Please upload shirt images first.
          </p>
          <p style={{ fontSize: '14px', color: '#a0aec0' }}>
            Tip: Upload shirt designs in Step 1 with image_type='shirt'
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-section">
      <div className="step-header-with-nav">
        <div className="navigation-buttons">
        </div>
        <h2 style={{ fontSize: '24px', margin: '0' }}>
          Choose Your Shirt Design
        </h2>
        <div className="navigation-buttons">
          <button 
            className="btn-next" 
            onClick={handleNext}
            disabled={!selectedDesign}
          >
            Next: Background →
          </button>
        </div>
      </div>
      <p style={{ textAlign: 'center', color: '#718096', marginBottom: '30px' }}>
        Select a shirt design from your uploaded collection
      </p>

      <div className="designs-grid">
        {shirts.map(shirt => (
          <div
            key={shirt.id}
            className={`design-card ${selectedDesign?.id === shirt.id ? 'selected' : ''}`}
            onClick={() => {
              setSelectedDesign(shirt);
              
              // Check if user is authenticated before auto-advancing
              if (!isAuthenticated) {
                setShowLoginModal(true);
                return;
              }
              
              setShouldAutoAdvance(true);
            }}
          >
            <div 
              className="design-preview"
              style={{
                backgroundImage: `url(${shirt.s3_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100%',
                height: '300px', // Increased height for vertical rectangle
                borderRadius: '8px'
              }}
            />
            <h3 style={{ marginTop: '10px', fontSize: '16px', fontWeight: '600' }}>
              {shirt.name || shirt.file_name || `Shirt ${shirt.id}`}
            </h3>
          </div>
        ))}
      </div>

      <div style={{ 
        marginTop: '30px', 
        padding: '20px', 
        background: '#f0fdf4', 
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#166534', fontSize: '14px' }}>
          💡 <strong>Tip:</strong> The selected shirt design will be applied to your photo using AI!
        </p>
      </div>

      {/* Authentication Modals */}
      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={closeModals}
          onSwitchToRegister={switchToRegister}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      
      {showRegisterModal && (
        <RegisterModal
          isOpen={showRegisterModal}
          onClose={closeModals}
          onSwitchToLogin={switchToLogin}
          onRegisterSuccess={handleLoginSuccess}
        />
      )}
    </div>
  );
};

export default DesignStep;
